/* ═════════════════════════ the storage port ═════════════════════════

   Everything studyHub knows lives in two localStorage keys. That is a fine
   place to keep it and a terrible place to keep it *only*: it is per-browser,
   per-device, and one "clear site data" from gone.

   This file is the seam. Nothing above it names `localStorage`; it asks a
   storage object to read and write text by key. Today the only implementation
   is the browser's, and swapping in another — IndexedDB when the images
   outgrow the quota, a server when there is one to talk to — is a new object
   with the same four methods rather than a search through seven thousand lines
   for every `setItem`.

   Putting the seam in costs almost nothing now. Retrofitting one after a
   backend exists costs a great deal, which is the whole argument for doing it
   before deciding anything about backends.                                   */

/** The two keys, named once. Images are kept apart from the rest because they
 *  are large enough to blow the quota on their own, and losing a background is
 *  survivable in a way that losing the term is not. */
export const KEYS = {
  main: 'studyhub.v1',
  images: 'studyhub.images.v1',
};

/** Thrown when a write fails for want of room, so callers can tell "the disk
 *  is full" from "the disk is broken" and say something useful either way. */
export class StorageFullError extends Error {
  constructor(key, cause) {
    super(`No room left to save ${key}`);
    this.name = 'StorageFullError';
    this.key = key;
    this.cause = cause;
  }
}

const isQuotaError = (err) =>
  err && (err.name === 'QuotaExceededError'
       || err.name === 'NS_ERROR_DOM_QUOTA_REACHED'
       || err.code === 22);

/**
 * The browser's own storage, wrapped.
 *
 * `available` is checked rather than assumed: Safari in private browsing hands
 * back a localStorage that throws on every write, and a planner that discovers
 * this at the moment the user finishes a task is a planner that loses the task.
 */
export function browserStorage(backing = globalThis.localStorage) {
  const available = (() => {
    try {
      const probe = '__studyhub_probe__';
      backing.setItem(probe, '1');
      backing.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  })();

  return {
    name: 'localStorage',
    available,

    read(key) {
      try { return backing.getItem(key); } catch { return null; }
    },

    write(key, text) {
      try {
        backing.setItem(key, text);
      } catch (err) {
        if (isQuotaError(err)) throw new StorageFullError(key, err);
        throw err;
      }
    },

    remove(key) {
      try { backing.removeItem(key); } catch { /* nothing to undo */ }
    },
  };
}

/** The one the app uses. Reassignable through `useStorage` so a test — or a
 *  future adapter — can stand somewhere else without the app knowing. */
let current = browserStorage();

export function useStorage(adapter) { current = adapter; }
export function storage() { return current; }
